import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { pushNotification } from "./useNotifications";

export type ConversationRow = Tables<"conversations">;
export type MessageRow = Tables<"messages">;

export function useConversations(userId: string | null) {
  return useQuery({
    queryKey: ["conversations", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("conversations")
        .select("*")
        .or(`participant_a.eq.${userId},participant_b.eq.${userId}`)
        .order("last_message_at", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as ConversationRow[];
    },
  });
}

export function useMessages(conversationId: string | null) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!conversationId) return;
    const channel = supabase
      .channel(`messages-${conversationId}-${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages", filter: `conversation_id=eq.${conversationId}` }, () => {
        qc.invalidateQueries({ queryKey: ["messages", conversationId] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [conversationId, qc]);

  return useQuery({
    queryKey: ["messages", conversationId],
    enabled: !!conversationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", conversationId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as MessageRow[];
    },
  });
}

/** Finds an existing conversation between two people or creates one. */
export async function getOrCreateConversation(meId: string, otherId: string): Promise<ConversationRow> {
  const { data: existing } = await supabase
    .from("conversations")
    .select("*")
    .or(`and(participant_a.eq.${meId},participant_b.eq.${otherId}),and(participant_a.eq.${otherId},participant_b.eq.${meId})`)
    .maybeSingle();
  if (existing) return existing as ConversationRow;

  const { data, error } = await supabase
    .from("conversations")
    .insert({ participant_a: meId, participant_b: otherId })
    .select()
    .single();
  if (error) throw error;
  return data as ConversationRow;
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, senderId, body, recipientId }: { conversationId: string; senderId: string; body: string; recipientId?: string }) => {
      const { error } = await supabase.from("messages").insert({ conversation_id: conversationId, sender_id: senderId, body });
      if (error) throw error;
      await supabase
        .from("conversations")
        .update({ last_message: body, last_message_at: new Date().toISOString() })
        .eq("id", conversationId);
      if (recipientId) {
        await pushNotification({ userId: recipientId, type: "message", title: "New message", body, link: "/messages" });
      }
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["messages", vars.conversationId] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}
